import { useState } from "react";
import { Link } from "wouter";
import { AuthLayout } from "../components/AuthLayout";

/**
 * /merchant/recover — Phase 17 entry page for tenant token recovery (D53).
 *
 * The merchant enters the email address on file with their tenant.
 * The server replies 200 with a generic message regardless of whether
 * the email exists, so this page can never be used to enumerate
 * registered tenant owners. If a match exists, an email arrives
 * within ~1 minute with a one-time link to /merchant/recover/verify.
 *
 * Three rate limits guard the endpoint (D62) — the merchant only sees
 * "Email sent" envelope; the actual ceilings (3/email/hour, 10/IP/hour,
 * 3/tenant/24h) are enforced server-side and surface as the same
 * friendly message after the limit is hit, matching the no-leak
 * guarantee of the happy path.
 */
export function MerchantTokenRecoveryPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setErr("Enter the email address on file with your tenant.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/v1/merchant/recover/request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) {
        // 4xx with structured envelope. We surface only validation
        // errors; the no-leak guarantee means rate-limits also return
        // 200 with the success copy server-side.
        const body = (await res.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        setErr(body?.error?.message ?? "Could not submit recovery request.");
        return;
      }
      setDone(true);
    } catch {
      setErr("Network error. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout
        title="Check your inbox"
        subtitle="If your tenant has an owner email on file, a recovery link is on its way."
      >
        <div className="space-y-4 text-sm text-ink-dark">
          <p>
            The link is valid for 15 minutes. Look in your spam folder if it
            doesn't arrive within a few minutes — the sender is{" "}
            <span className="font-mono">no-reply@orbit.m42.ph</span>.
          </p>
          <p>
            If you don't have an owner email on file yet, contact M42 ops to
            backfill one through the Console — recovery isn't available until
            then.
          </p>
          <Link href="/merchant/login" className="btn-secondary block w-full text-center">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Recover your API token"
      subtitle="We'll send a one-time recovery link to your tenant owner email."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="recover-email" className="label">
            Owner email
          </label>
          <input
            id="recover-email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            placeholder="owner@yourbusiness.ph"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
          />
          <p className="mt-1 text-xs text-ink-dark">
            We never reveal whether an address has a tenant on file. The
            response is the same either way.
          </p>
        </div>
        {err && (
          <p className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
            {err}
          </p>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? "Sending…" : "Send recovery link"}
        </button>
        <Link
          href="/merchant/login"
          className="block text-center text-sm text-navy underline"
        >
          Back to sign in
        </Link>
      </form>
    </AuthLayout>
  );
}
